import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase.config';

// Enhanced phone number validation with better error handling
export const isPhone = (v) => {
  if (!v) return false;
  const trimmed = v.trim();
  // More comprehensive phone validation
  return /^\+?[1-9]\d{9,14}$/.test(trimmed);
};

// Validate phone number format and return detailed validation result
export const validatePhoneNumber = (phone) => {
  if (!phone) {
    return {
      isValid: false,
      error: 'Phone number is required',
      normalized: '',
      suggestions: []
    };
  }

  const trimmed = phone.trim();
  
  // Check for empty string
  if (!trimmed) {
    return {
      isValid: false,
      error: 'Phone number cannot be empty',
      normalized: '',
      suggestions: []
    };
  }

  // Check for invalid characters
  if (!/^[\d\+\-\s\(\)]+$/.test(trimmed)) {
    return {
      isValid: false,
      error: 'Phone number contains invalid characters',
      normalized: '',
      suggestions: ['Remove special characters except +, -, (, ), and spaces']
    };
  }

  // Remove all non-digit characters except +
  const digitsOnly = trimmed.replace(/[^\d\+]/g, '');
  
  // Check if it starts with + but has invalid format
  if (digitsOnly.startsWith('+') && digitsOnly.length < 11) {
    return {
      isValid: false,
      error: 'International phone number too short',
      normalized: '',
      suggestions: ['International numbers should be at least 11 digits (e.g., +1234567890)']
    };
  }

  // Check if it's a valid length
  if (digitsOnly.length < 10 || digitsOnly.length > 15) {
    return {
      isValid: false,
      error: 'Phone number must be between 10 and 15 digits',
      normalized: '',
      suggestions: [
        'For Indian numbers: 10 digits (e.g., 9876543210)',
        'For international numbers: 11-15 digits with country code (e.g., +1234567890)'
      ]
    };
  }

  // Normalize the phone number
  const normalized = normalizePhoneNumber(digitsOnly);
  
  // Final validation
  if (!isPhone(normalized)) {
    return {
      isValid: false,
      error: 'Invalid phone number format',
      normalized: '',
      suggestions: ['Please check the phone number format']
    };
  }

  return {
    isValid: true,
    error: null,
    normalized: normalized,
    suggestions: []
  };
};

// Function to check if phone number already exists with retry logic
export const checkPhoneExists = async (phoneNumber, currentUserId = null, maxRetries = 3) => {
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      console.log(`🔍 Checking phone uniqueness for: ${phoneNumber} (attempt ${retryCount + 1}/${maxRetries})`);
      
      // Validate phone number first
      const validation = validatePhoneNumber(phoneNumber);
      if (!validation.isValid) {
        return {
          exists: false,
          error: validation.error,
          suggestions: validation.suggestions
        };
      }

      const normalizedPhone = validation.normalized;
      
      // Check in phone_index collection
      const phoneIndexRef = doc(db, 'phone_index', normalizedPhone);
      const phoneIndexSnap = await getDoc(phoneIndexRef);
      
      if (phoneIndexSnap.exists()) {
        const data = phoneIndexSnap.data();
        const existingUserId = data.userId || data.uid; // Support both field names
        
        // If phone belongs to current user, it's okay
        if (currentUserId && existingUserId === currentUserId) {
          console.log('✅ Phone belongs to current user');
          return { exists: false, normalized: normalizedPhone };
        }
        
        console.log('❌ Phone already exists for user:', existingUserId);
        return { 
          exists: true, 
          userId: existingUserId,
          message: 'This phone number is already registered with another account',
          normalized: normalizedPhone
        };
      }
      
      // Also check in users collection as backup
      const usersQuery = query(
        collection(db, 'users'),
        where('phoneNumber', '==', normalizedPhone)
      );
      const usersSnap = await getDocs(usersQuery);
      
      if (!usersSnap.empty) {
        const existingUser = usersSnap.docs[0];
        const existingUserId = existingUser.id;
        
        // If phone belongs to current user, it's okay
        if (currentUserId && existingUserId === currentUserId) {
          console.log('✅ Phone belongs to current user (users collection)');
          return { exists: false, normalized: normalizedPhone };
        }
        
        console.log('❌ Phone already exists in users collection for:', existingUserId);
        return { 
          exists: true, 
          userId: existingUserId,
          message: 'This phone number is already registered with another account',
          normalized: normalizedPhone
        };
      }
      
      console.log('✅ Phone number is unique');
      return { exists: false, normalized: normalizedPhone };
      
    } catch (error) {
      retryCount++;
      console.error(`Error checking phone uniqueness (attempt ${retryCount}/${maxRetries}):`, error);
      
      if (retryCount >= maxRetries) {
        return { 
          exists: false, // Allow in case of error to not block user
          error: 'Unable to verify phone uniqueness after multiple attempts',
          suggestions: ['Please try again or contact support if the problem persists']
        };
      }
      
      // Wait before retry (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
    }
  }
  
  return { 
    exists: false,
    error: 'Max retries exceeded',
    suggestions: ['Please try again later']
  };
};

// Normalize phone number to E164 format with better error handling
export const normalizePhoneNumber = (phone) => {
  if (!phone) return '';
  
  const trimmed = phone.trim();
  
  // Remove all non-digit characters except +
  const digitsOnly = trimmed.replace(/[^\d\+]/g, '');
  
  // If it already starts with +, return as is (assuming it's valid)
  if (digitsOnly.startsWith('+')) {
    return digitsOnly;
  }
  
  // Handle different country codes
  if (digitsOnly.length === 10) {
    // Assume Indian number if 10 digits
    return `+91${digitsOnly}`;
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    // Assume US number if 11 digits starting with 1
    return `+${digitsOnly}`;
  } else if (digitsOnly.length >= 10) {
    // For other cases, assume it needs a country code
    // Default to India (+91) for now, but this could be made configurable
    return `+91${digitsOnly}`;
  }
  
  // If we can't determine the format, return the original
  return digitsOnly;
};

// Format phone number for display
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  const normalized = normalizePhoneNumber(phone);
  
  // Format Indian numbers (+91XXXXXXXXXX)
  if (normalized.startsWith('+91') && normalized.length === 13) {
    const number = normalized.slice(3);
    return `+91 ${number.slice(0, 5)} ${number.slice(5)}`;
  }
  
  // Format US numbers (+1XXXXXXXXXX)
  if (normalized.startsWith('+1') && normalized.length === 12) {
    const number = normalized.slice(2);
    return `+1 (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
  }
  
  // For other international numbers, just add spaces every 3 digits
  if (normalized.length > 4) {
    return normalized.replace(/(\d{3})(?=\d)/g, '$1 ');
  }
  
  return normalized;
};

